import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus } from 'lucide-react'

import { CreateInvite } from './AddInviteModal'
import { EditInvite } from './EditInviteModal'
import Loader from '../../../common/components/Loader'
import { Currency } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getCurrencies,
  getSettingsTickets,
} from '../../../core/services/festival-settings'
import {
  IInvite,
  InvitesPaginationParams,
  getAllInvites,
  updateInvite,
} from '../../../core/services/invites'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { addDays, subWeeks } from 'date-fns'
import { Icon } from '@/common/components/Icon'

export const InvitesPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams({
    defaultSize: 20,
  })

  const { hasEditPermission } = useCheckPermission({ page: 'INVITES' })

  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [addNew, setAddNew] = useState<boolean>(false)
  const [editInvites, setEditInvites] = useState<boolean>(false)
  const [currentInvite, setCurrentInvite] = useState<IInvite>()

  const [invites, setInvites] = useState<IInvite[]>([])
  const [startDate, setStartDate] = useState<number>(
    subWeeks(new Date(), 1).setHours(0, 0, 0).valueOf(),
  )
  const [endDate, setEndDate] = useState<number>(
    addDays(new Date(), 1).setHours(23, 59, 59).valueOf(),
  )
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [currency, setCurrency] = useState<Currency | null>(null)

  const [dubaiIntegration, setDubaiIntegration] = useState<boolean>(false)

  const timer = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useRef<{ [key: string]: string | null }>({})
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const { data, status } = await getSettingsTickets()
        const response = await getCurrencies()
        if (status === 200) {
          setDubaiIntegration(data.dubaiIntegration)
        }
        if (response.status === 200) {
          setCurrency(response.data.defaultCurrency)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    const params: InvitesPaginationParams = {
      page,
      size,
      startDate,
      endDate,
      sortBy,
      sortDirection,
    }

    if (searchParams.current.redeemCode) {
      params.redeemCode = searchParams.current.redeemCode
    }
    try {
      setLoading(true)
      const { data, status } = await getAllInvites(params)
      if (status === 200) {
        setInvites(data.content)
        setTotalElemenets(data.totalElements)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    startDate,
    endDate,
    sortBy,
    sortDirection,
    editionId,
    festivalName,
    setTotalElemenets,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        setLoading(true)
        try {
          const params: InvitesPaginationParams = {
            page,
            size,
            startDate,
            endDate,
          }

          if (searchParams.current.redeemCode) {
            params.redeemCode = searchParams.current.redeemCode
          }

          const { data, status } = await getAllInvites(params)
          if (status === 200) {
            setInvites(data.content)
            setTotalElemenets(data.totalElements)
          }
        } catch (err) {
          console.log(err)
        } finally {
          setLoading(false)
        }
      }, 2000)
      timer.current = newTimer
    },
    [page, size, sortBy],
  )

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(invites.map((one) => one.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  // const handleRedeemCheck = async (row: IInvite) => {
  //   try {
  //     const { data, status } = await updateInvite(row.id, {
  //       freeRedeem: !row.freeRedeem,
  //     })
  //     if (status === 200) {
  //       setInvites((s) => s.map((one) => (one.id === row.id ? data : one)))
  //     }
  //   } catch (err) {
  //     console.log(err)
  //   }
  // }

  const handleCheckinCheck = async (row: IInvite) => {
    if (!hasEditPermission) {
      return
    }
    try {
      const { data, status } = await updateInvite(row.id, {
        freeCheckin: !row.freeCheckin,
      })
      if (status === 200) {
        setInvites((s) => s.map((one) => (one.id === row.id ? data : one)))
      }
    } catch (err) {
      console.log(err)
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
      width: '16px',
    },
    {
      title: 'Id',
      accessor: 'id',
      sortable: true,
      render: (row: IInvite) => row.id.substring(0, 4),
      width: '40px',
    },
    {
      title: 'Label Name',
      accessor: 'label',
      width: '15%',
    },
    {
      title: 'Redeem Code',
      accessor: 'redeemCode',
      searchable: true,
    },
    {
      title: 'Tickets',
      accessor: '',
      render: (row: IInvite) => row.ticket.ticket.name,
    },
    {
      title: 'Generated Date',
      accessor: '',
      render: (row: IInvite) =>
        row.generatedTimestamp
          ? formatDateTime(row.generatedTimestamp, 'dd.MM.yyyy HH:mm')
          : '-',
    },
    // {
    //   title: 'Modified by',
    //   accessor: '',
    //   render: (row: IInvite) =>
    //     row?.user ? row?.user?.firstName + ' ' + row?.user?.lastName : '-',
    // },
    {
      title: 'Sent to',
      accessor: '',
      render: (row: IInvite) => (
        <>
          {row.user && (
            <div>{`${row.user.firstName} ${row.user.lastName}`}</div>
          )}
          {row.email && <div style={{ color: '#6C757D' }}>{row.email}</div>}
        </>
      ),
    },
    {
      title: 'Used',
      accessor: '',
      render: (row: IInvite) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Checkbox disabled checked={row.used || false} />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-1">
                <span>{`Name: ${
                  row.ticketsUsedOn.filter((t) => t.ticketOwner)[0]
                    ?.boughtForName ||
                  row?.ticketsUsedOn?.[0]?.boughtForName ||
                  ''
                }`}</span>
                <span>{`Phone: ${
                  row.ticketsUsedOn.filter((t) => t.ticketOwner)[0]
                    ?.boughtForPhone ||
                  row?.ticketsUsedOn?.[0]?.boughtForPhone ||
                  ''
                }`}</span>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      title: 'Used Date',
      accessor: '',
      render: (row: IInvite) =>
        row.usedDateTimestamp
          ? formatDateTime(row.usedDateTimestamp, 'dd.MM.yyyy HH:mm')
          : '-',
    },
    {
      title: 'Price',
      accessor: '',
      render: (row: IInvite) => `${row.price} ${currency?.symbol}`,
    },
    {
      title: 'Redeem Tax',
      accessor: '',
      render: (row: IInvite) => `${row.tax} ${currency?.symbol}`,
    },
    {
      title: 'Free Checkin',
      accessor: '',
      render: (row: IInvite) => (
        <Checkbox
          checked={row.freeCheckin || false}
          disabled={!hasEditPermission}
          onCheckedChange={() => handleCheckinCheck(row)}
        />
      ),
    },
    {
      title: 'View',
      accessor: '',
      render: (row: IInvite) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setEditInvites(true)
            setCurrentInvite(row)
          }}
        >
          View
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Rewards"
        actions={
          <>
            {hasEditPermission && isCheck.length > 0 && (
              <Button
                variant="secondary"
                type="button"
                className="border border-monochrome-120"
              >
                Export Data CSV
              </Button>
            )}
            {hasEditPermission && (
              <Button onClick={() => setAddNew(true)}>
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle
          title="Rewards"
          subtitle="Invites / Tickets"
          className="mb-5"
        />
        <Table
          handleSearch={handleSearchParams}
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={invites}
          noDataMessage={
            searchParams.current.redeemCode ? (
              <span>
                Sorry, <b>Redeem Code</b> not found
              </span>
            ) : (
              <span>Sorry, There are no data</span>
            )
          }
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <CreateInvite
        dubaiIntegration={dubaiIntegration}
        visible={addNew}
        currency={currency?.symbol || 'RON'}
        setVisible={setAddNew}
        onSuccess={getData}
      />
      <EditInvite
        dubaiIntegration={dubaiIntegration}
        visible={editInvites}
        currency={currency?.symbol || 'RON'}
        setVisible={setEditInvites}
        invite={currentInvite}
        getData={getData}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
